import ideas from "./automation-ideas.json" with { type: "json" };
import meta from "./automation-ideas-meta.json" with { type: "json" };
import { ideaHubPath, ideaParentHub } from "./automationIdeaHubs.ts";

export type AutomationIdea = (typeof ideas)[number];

// Generated by the workflow catalog research tooling; see docs/internals/workflow-catalog.md.
export const automationIdeas = ideas;

/** When the workflow content was last rebuilt and reviewed, as YYYY-MM-DD. */
export const workflowsUpdated = meta.updated;

export const automationTriggers: Record<string, string> = {
  ask: "When you ask",
  schedule: "On a schedule",
  event: "When something happens",
};

export const ideaPath = (idea: AutomationIdea) => `/workflows/${idea.slug}`;

export function findIdea(slug: string) {
  return automationIdeas.find((idea) => idea.slug === slug);
}

export function ideaFromPath(path: string) {
  const match = /^\/workflows\/([a-z0-9-]+)$/.exec(path);
  return match ? findIdea(match[1]!) : undefined;
}

/** Search fields written for a workflow page from real search data. */
export type WorkflowSeo = {
  readonly seo_title: string;
  readonly meta: string;
  readonly question: string;
  readonly answer: string;
  readonly faq: readonly { readonly q: string; readonly a: string }[];
  readonly limits: string;
};

/** The heavy written content for one workflow, loaded only on its own page. */
export type IdeaDetails = {
  /** How the work is done by hand today. */
  readonly today: string;
  readonly seo: WorkflowSeo | null;
  readonly demo: WorkflowDemo | null;
};

export type WorkflowIdea = AutomationIdea & IdeaDetails;

export const withDetails = (
  idea: AutomationIdea,
  details: IdeaDetails | undefined,
): WorkflowIdea => ({
  ...idea,
  today: details?.today ?? "",
  seo: details?.seo ?? null,
  demo: details?.demo ?? null,
});

/** Search identity for one idea's page, with breadcrumbs and questions for search engines. */
export function ideaPage(idea: WorkflowIdea) {
  const path = ideaPath(idea);
  const parent = ideaParentHub(idea);
  return {
    kind: "marketing" as const,
    path,
    title: idea.seo?.seo_title ?? `${idea.title} with AI`,
    description:
      idea.seo?.meta ??
      `${idea.pitch} See the steps, an example, and how to set it up in Slack or by email.`,
    updated: workflowsUpdated,
    structuredData: (origin: string) => [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Workflows", item: `${origin}/workflows` },
          ...(parent
            ? [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: parent.value,
                  item: `${origin}${ideaHubPath(parent)}`,
                },
              ]
            : []),
          {
            "@type": "ListItem",
            position: parent ? 3 : 2,
            name: idea.title,
            item: `${origin}${path}`,
          },
        ],
      },
      {
        "@type": "HowTo",
        name: idea.title,
        description: idea.pitch,
        step: idea.steps.map((text, index) => ({
          "@type": "HowToStep",
          position: index + 1,
          name: text,
          text,
        })),
      },
      ...(idea.seo
        ? [
            {
              "@type": "FAQPage",
              mainEntity: [{ q: idea.seo.question, a: idea.seo.answer }, ...idea.seo.faq].map(
                (item) => ({
                  "@type": "Question",
                  name: item.q,
                  acceptedAnswer: { "@type": "Answer", text: item.a },
                }),
              ),
            },
          ]
        : []),
    ],
  };
}

/** Other ideas for the same team, so each page leads somewhere useful. */
export function relatedIdeas(idea: AutomationIdea, count = 6) {
  return automationIdeas
    .filter((other) => other.id !== idea.id && other.team === idea.team)
    .slice(0, count);
}

export type WorkflowMessage = {
  readonly from: string;
  readonly role: "person" | "maito";
  readonly time: string;
  readonly text: string;
};

/** A written example run and the request that sets the workflow up. */
export type WorkflowDemo = {
  readonly setup: { readonly request: string; readonly confirmation: string } | null;
  readonly example: {
    readonly channel: string;
    readonly subject: string;
    /** What started a Loop run, such as "Monday 9:00 AM · Weekly pipeline review". */
    readonly trigger: string | null;
    readonly messages: readonly WorkflowMessage[];
  };
};

export const workflowDemo = (idea: AutomationIdea | WorkflowIdea) =>
  "demo" in idea ? idea.demo : null;
